import styled from "@emotion/styled";
import type { TokenInfo } from "@stepfinance/common";
import { darkGreen, darkGrey, formatBalance, transparentWhite } from "@stepfinance/react";
import type { FC } from "react";
import React, { useMemo } from "react";

import { useMarkets } from "../../contexts/market";
import { useCalculatePriceForDisplayCurrency } from "../../hooks";
import { SwapTokenInfoDisplay } from "./SwapTokenInfoDisplay";

const TokenListRowDiv = styled.div<{
  hasBalance: boolean;
  selected: boolean;
  disabled: boolean;
}>`
  display: flex;
  justify-content: space-between;
  background: ${({ selected, disabled }) => (selected ? darkGreen : disabled ? darkGrey : "#000000")};
  padding: 10px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  color: ${({ disabled }) => (disabled ? "#434343" : "#ffffff")};
  .token-balance {
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    font-family: Space Mono;
    color: ${({ hasBalance, disabled }) => (!hasBalance || disabled ? "#434343" : "#ffffff")};
  }

  :hover {
    background-color: ${({ disabled }) => (disabled ? darkGrey : darkGreen)};
  }
`;

type TokenListRowProps = Pick<TokenInfo, "address" | "symbol" | "decimals" | "name"> & {
  selected: boolean;
  balance?: number;
  onMintSelected: (mintAddress: string) => void;
  disabled: boolean;
};

type TokenBalanceProps = {
  mint: string;
  balance?: number;
  decimals: number;
};

type TokenNameProps = {
  mint: string;
  symbol: string;
  name: string;
};

export const TokenListRow: FC<TokenListRowProps> = React.memo(
  ({ address, symbol, selected, balance, onMintSelected, disabled, decimals, name }) => {
    return (
      <TokenListRowDiv
        key={address}
        hasBalance={!!balance}
        selected={selected}
        onClick={() => (disabled ? null : onMintSelected(address))}
        disabled={disabled}
      >
        <TokenName mint={address} name={name} symbol={symbol} />
        <TokenBalance decimals={decimals} mint={address} balance={balance} />
      </TokenListRowDiv>
    );
  },
);
TokenListRow.displayName = "TokenListRow";

const TokenBalance: FC<TokenBalanceProps> = ({ mint, balance, decimals }) => {
  const { priceInUSD, marketsInitialised } = useMarkets();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const usdValue = useMemo(() => {
    if (priceInUSD && marketsInitialised) {
      const price = priceInUSD(mint);
      return (price ?? 0) * (balance ?? 0);
    }
    return 0;
  }, [balance, mint, priceInUSD, marketsInitialised]);

  return (
    <TokenBalanceContainer>
      <span className="token-balance">{formatBalance(balance ?? 0, decimals)}</span>
      <span className="usd-value">{calculatePriceForDisplayCurrency(usdValue, { hideable: false })}</span>
    </TokenBalanceContainer>
  );
};

const TokenName: FC<TokenNameProps> = ({ mint, name, symbol }) => {
  return (
    <TokenNameContainer>
      <TokenListInfoDisplay mint={mint} symbol={symbol} />
      <span className="token-name">{name}</span>
    </TokenNameContainer>
  );
};

const TokenNameContainer = styled.div`
  display: flex;
  flex-direction: column;

  .token-name {
    color: ${transparentWhite};
    font-size: 10px;
  }
`;

const TokenBalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  .token-balance {
    margin: 0;
  }

  .usd-value {
    font-family: Space Mono;
    font-size: 10px;
    color: ${transparentWhite} !important;
  }
`;

const TokenListInfoDisplay = styled(SwapTokenInfoDisplay)`
  img {
    width: 16px;
    height: 16px;
  }

  span {
    margin-left: 10px !important;
    font-size: 14px;
  }
`;
